/**
 * ItemForm Component
 * 
 * Reusable form component for editing item details.
 * Used by both mobile and desktop layouts.
 * Supports full mode (all fields) and guest mode (name + description only).
 */

import React, { useState, useEffect } from 'react';
import { EditableItem } from './types';
import { CategorySelectorModal } from '../CategorySelectorModal';
import { getCategories, Category } from '../../services/categoryService';
import { PriceInput } from '../PriceInput';
import { useSpace } from '../../contexts/SpaceContext';

export interface ItemFormProps {
  item: EditableItem;
  onChange: (patch: Partial<EditableItem>) => void;
  token?: string;
  readOnly?: boolean;
  mode?: 'full' | 'guest';
}

// Double-click to select all text
const doubleClickSelectProps = {
  onDoubleClick: (e: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    e.currentTarget.select();
  },
};

// Scroll field into view when keyboard opens on mobile
const scrollOnFocus = {
  onFocus: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const el = e.currentTarget;
    setTimeout(() => {
      el.parentElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 350);
  },
};

export const ItemForm: React.FC<ItemFormProps> = ({
  item,
  onChange,
  token,
  readOnly = false,
  mode = 'full',
}) => {
  const { spaces } = useSpace();
  // Track collapsible section state - must be before any conditional returns
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [locationExpanded, setLocationExpanded] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [categoryLabels, setCategoryLabels] = useState<string[]>([]);

  // Fetch all categories on mount
  useEffect(() => {
    if (token) {
      getCategories(token, { flat: true })
        .then(cats => setAllCategories(cats))
        .catch(err => console.error('Failed to fetch categories:', err));
    }
  }, [token]);

  // Update category labels when categories change
  useEffect(() => {
    if (item.categories && allCategories.length > 0) {
      const labels = item.categories
        .map(c => allCategories.find(cat => cat.id === c.id)?.label)
        .filter(Boolean) as string[];
      setCategoryLabels(labels);
    } else {
      setCategoryLabels([]);
    }
  }, [item.categories, allCategories]);

  // Check if details have values (to show indicator dot)
  const hasDetailsValues = (item.quantity !== undefined && item.quantity !== 1) || 
                           item.color || 
                           item.condition ||
                           (item as any).purchase_price || 
                           (item as any).purchased_at;
  
  // Check if location has values
  const hasLocationValues = item.area || item.location || (item as any).sub_location || item.space_id;

  // Guest mode: only name and description
  if (mode === 'guest') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Name */}
        <div>
          <label className="label-standard">Name</label>
          <input
            type="text"
            className="input-standard"
            value={item.name}
            onChange={(e) => onChange({ name: e.target.value })}
            {...doubleClickSelectProps}
            placeholder="Item name"
            required
          />
        </div>

        {/* Description */}
        <div>
          <label className="label-standard">Description</label>
          <textarea
            className="input-standard"
            style={{ minHeight: 72, resize: 'vertical' }}
            value={item.description}
            onChange={(e) => onChange({ description: e.target.value })}
            {...doubleClickSelectProps}
            placeholder="Optional description"
          />
        </div>
      </div>
    );
  }

  // Full mode: all fields - compact layout with collapsible sections
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Name */}
      <div>
        <label className="label-standard">Name</label>
        <input
          type="text"
          className="input-standard"
          value={item.name}
          onChange={(e) => onChange({ name: e.target.value })}
          {...doubleClickSelectProps}
          placeholder="Item name"
          readOnly={readOnly}
          disabled={readOnly}
          style={readOnly ? { backgroundColor: 'var(--color-bg-tertiary)', cursor: 'not-allowed' } : undefined}
          required
        />
      </div>

      {/* Description - larger for more text visibility */}
      <div style={{ flex: 1 }}>
        <label className="label-standard">Description</label>
        <textarea
          className="input-standard"
          style={readOnly ? { minHeight: 150, resize: 'vertical', backgroundColor: 'var(--color-bg-tertiary)', cursor: 'not-allowed' } : { minHeight: 150, resize: 'vertical' }}
          value={item.description}
          onChange={(e) => onChange({ description: e.target.value })}
          {...doubleClickSelectProps}
          placeholder="Optional description"
          readOnly={readOnly}
          disabled={readOnly}
        />
      </div>

      {/* Categories - button to open modal */}
      {token && (
        <div>
          <label className="label-standard">Categories</label>
          {readOnly ? (
            <div style={{ padding: '12px', backgroundColor: 'var(--color-bg-tertiary)', borderRadius: '8px', color: 'var(--color-text-muted)' }}>
              {item.categories && item.categories.length > 0 ? `${item.categories.length} categories selected` : 'No categories'}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowCategoryModal(true)}
              style={{
                width: '100%',
                padding: '10px 14px',
                border: '1px solid var(--color-border)',
                borderRadius: '10px',
                background: 'var(--color-bg-primary)',
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: '14px',
                color: categoryLabels.length > 0 ? 'var(--color-text-secondary)' : 'var(--color-text-faint)',
              }}
            >
              {categoryLabels.length > 0 ? categoryLabels.join(', ') : 'Tap to select categories...'}
            </button>
          )}
        </div>
      )}

      {/* Category Selector Modal */}
      {token && (
        <CategorySelectorModal
          isOpen={showCategoryModal}
          onClose={() => setShowCategoryModal(false)}
          selectedCategoryIds={item.categories?.map(c => c.id) || []}
          onChange={(categoryIds: number[]) => {
            onChange({ 
              categories: categoryIds.map((id: number) => ({ 
                id, 
                key: '', 
                label: '' 
              })) 
            });
          }}
          token={token}
          allCategories={allCategories}
        />
      )}

      {/* Details - Collapsible */}
      <button 
        type="button"
        onClick={() => setDetailsExpanded(!detailsExpanded)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 0',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 600,
          color: 'var(--color-text-secondary)',
        }}
      >
        <svg 
          width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          style={{ transform: detailsExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
        <span>Details</span>
        {!detailsExpanded && hasDetailsValues && (
          <span style={{ color: 'var(--color-accent-secondary)', fontSize: '18px', lineHeight: 1 }}>•</span>
        )}
      </button>
      {detailsExpanded && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingLeft: '24px' }}>
          {/* Color and Quantity Row */}
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label className="label-standard">Color</label>
              <input
                className="input-standard"
                value={item.color || ''}
                onChange={(e) => onChange({ color: e.target.value })}
                {...doubleClickSelectProps}
                {...scrollOnFocus}
                placeholder="e.g., red"
                readOnly={readOnly}
                disabled={readOnly}
                style={readOnly ? { backgroundColor: 'var(--color-bg-tertiary)', cursor: 'not-allowed' } : undefined}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label className="label-standard">Quantity</label>
              <input
                className="input-standard"
                type="number"
                min={0}
                value={item.quantity ?? 1}
                onChange={(e) => onChange({ quantity: Number(e.target.value) })}
                {...doubleClickSelectProps}
                {...scrollOnFocus}
                placeholder="1"
                readOnly={readOnly}
                disabled={readOnly}
                style={readOnly ? { backgroundColor: 'var(--color-bg-tertiary)', cursor: 'not-allowed' } : undefined}
              />
            </div>
          </div>

          {/* Condition and Qty Min Row */}
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label className="label-standard">Condition</label>
              <select
                className="input-standard"
                value={item.condition || ''}
                onChange={(e) => onChange({ condition: e.target.value || undefined })}
                disabled={readOnly}
                style={{
                  ...(readOnly ? { backgroundColor: 'var(--color-bg-tertiary)', cursor: 'not-allowed' } : {}),
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 10px center',
                  paddingRight: '28px',
                }}
              >
                <option value="">—</option>
                <option value="new">New</option>
                <option value="like_new">Like New</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
                <option value="poor">Poor</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label className="label-standard">Qty Min</label>
              <input
                className="input-standard"
                type="number"
                min={0}
                value={item.quantity_min ?? ''}
                onChange={(e) => onChange({ quantity_min: e.target.value ? Number(e.target.value) : undefined })}
                {...doubleClickSelectProps}
                {...scrollOnFocus}
                placeholder="—"
                readOnly={readOnly}
                disabled={readOnly}
                style={readOnly ? { backgroundColor: 'var(--color-bg-tertiary)', cursor: 'not-allowed' } : undefined}
              />
            </div>
          </div>

          {/* Price Row */}
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label className="label-standard">Purchase Price</label>
              <PriceInput
                className="input-standard"
                value={(item as any).purchase_price}
                onChange={(cents) => onChange({ purchase_price: cents } as any)}
                readOnly={readOnly}
                disabled={readOnly}
                style={readOnly ? { backgroundColor: 'var(--color-bg-tertiary)', cursor: 'not-allowed' } : undefined}
                onFocus={scrollOnFocus.onFocus}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label className="label-standard">Sale Price</label>
              <PriceInput
                className="input-standard"
                value={(item as any).sale_price}
                onChange={(cents) => onChange({ sale_price: cents } as any)}
                readOnly={readOnly}
                disabled={readOnly}
                style={readOnly ? { backgroundColor: 'var(--color-bg-tertiary)', cursor: 'not-allowed' } : undefined}
                onFocus={scrollOnFocus.onFocus}
              />
            </div>
          </div>
          {/* Purchase Date Row — half-width to align with Price row above */}
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label className="label-standard">Purchased At</label>
              <input
                className="input-standard"
                type="date"
                {...scrollOnFocus}
                style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', ...(readOnly ? { backgroundColor: 'var(--color-bg-tertiary)', cursor: 'not-allowed' } : {}) }}
                value={(() => {
                  const date = (item as any).purchased_at;
                  if (!date) return '';
                  try {
                    const d = new Date(date);
                    const year = d.getFullYear();
                    const month = String(d.getMonth() + 1).padStart(2, '0');
                    const day = String(d.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                  } catch (e) {
                    return '';
                  }
                })()}
                onChange={(e) => {
                  if (e.target.value) {
                    // Store as ISO string with noon time to avoid timezone date shifts
                    onChange({ purchased_at: `${e.target.value}T12:00:00` } as any);
                  } else {
                    onChange({ purchased_at: null } as any);
                  }
                }}
                readOnly={readOnly}
                disabled={readOnly}
              />
            </div>
            <div style={{ flex: 1 }} />
          </div>
        </div>
      )}

      {/* Location - Collapsible */}
      <button 
        type="button"
        onClick={() => setLocationExpanded(!locationExpanded)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 0',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 600,
          color: 'var(--color-text-secondary)',
        }}
      >
        <svg 
          width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          style={{ transform: locationExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
        <span>Location</span>
        {!locationExpanded && hasLocationValues && (
          <span style={{ color: 'var(--color-accent-secondary)', fontSize: '18px', lineHeight: 1 }}>•</span>
        )}
      </button>
      {locationExpanded && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingLeft: '24px' }}>
          {/* Area and Location Row */}
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label className="label-standard">Area</label>
              <input
                className="input-standard"
                value={item.area || ''}
                onChange={(e) => onChange({ area: e.target.value })}
                {...doubleClickSelectProps}
                {...scrollOnFocus}
                placeholder="e.g., kitchen"
                readOnly={readOnly}
                disabled={readOnly}
                style={readOnly ? { backgroundColor: 'var(--color-bg-tertiary)', cursor: 'not-allowed' } : undefined}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label className="label-standard">Location</label>
              <input
                className="input-standard"
                value={item.location || ''}
                onChange={(e) => onChange({ location: e.target.value })}
                {...doubleClickSelectProps}
                {...scrollOnFocus}
                placeholder="e.g., island"
                readOnly={readOnly}
                disabled={readOnly}
                style={readOnly ? { backgroundColor: 'var(--color-bg-tertiary)', cursor: 'not-allowed' } : undefined}
              />
            </div>
          </div>

          {/* Sub-location */}
          <div>
            <label className="label-standard">Sub-location</label>
            <input
              className="input-standard"
              value={(item as any).sub_location || ''}
              onChange={(e) => onChange({ sub_location: e.target.value } as any)}
              {...doubleClickSelectProps}
              {...scrollOnFocus}
              placeholder="e.g., drawer 2"
              readOnly={readOnly}
              disabled={readOnly}
              style={readOnly ? { backgroundColor: 'var(--color-bg-tertiary)', cursor: 'not-allowed' } : undefined}
            />
          </div>

          {/* Space Selection */}
          <div>
            <label className="label-standard">Space (Organization)</label>
            <select
              className="input-standard"
              value={item.space_id || ''}
              onChange={(e) => onChange({ space_id: Number(e.target.value) })}
              disabled={readOnly}
              style={{
                ...(readOnly ? { backgroundColor: 'var(--color-bg-tertiary)', cursor: 'not-allowed' } : {}),
                appearance: 'none',
                WebkitAppearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 10px center',
                paddingRight: '28px',
              }}
            >
              {spaces.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default ItemForm;
